import { useEffect, useRef } from 'react';

export default function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const imageLeftRef = useRef<HTMLDivElement>(null);
  const imageRightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      if (imageLeftRef.current) {
        imageLeftRef.current.style.transform = `translateY(${scrollY * 0.05}px)`;
      }
      if (imageRightRef.current) {
        imageRightRef.current.style.transform = `translateY(${scrollY * -0.03}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.animate-item');
            elements.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('animate-fade-in-up');
                (el as HTMLElement).style.opacity = '1';
              }, i * 150);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="nosotros" ref={sectionRef} className="relative bg-aa-black py-16 md:py-24 overflow-hidden">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Text Section */}
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 mb-12 md:mb-20">
            <div className="lg:w-1/3">
              <span className="animate-item opacity-0 label-tag block mb-4">QUIÉNES SOMOS</span>
            </div>
            <div className="lg:w-2/3">
              <h2 className="animate-item opacity-0 heading-display text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                Construimos valor
                <br />
                desde los <span className="gold-text-gradient">Andes</span>
              </h2>
              <p className="animate-item opacity-0 text-aa-gray-100 leading-relaxed max-w-2xl">
                Alianza Andina Empresarial es una firma de financiamiento privado especializada en estructurar soluciones de capital para proyectos de infraestructura, agroindustria, hotelería y desarrollo empresarial en Latinoamérica. Nuestro modelo de negocio se fundamenta en tres pilares: respaldo con cuentas bancarias o bienes raíces, plazos flexibles hasta 8 años, y un proceso profesional, seguro y transparente.
              </p>
              <p className="animate-item opacity-0 text-aa-gray-200 leading-relaxed max-w-2xl mt-4">
                Operamos con presencia en toda la región andina, conectando capital institucional con proyectos de alto impacto. Cada estructura financiera es diseñada a la medida, garantizando la viabilidad y rentabilidad del proyecto desde el primer día.
              </p>
            </div>
          </div>

          {/* Images Section */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6">
            <div className="md:col-span-2 overflow-hidden rounded-2xl border border-aa-gold/20">
              <div ref={imageLeftRef} className="parallax-image">
                <img
                  src="https://readdy.ai/api/search-image?query=Executive%20business%20meeting%20in%20modern%20boardroom%2C%20Latin%20American%20professionals%20in%20formal%20suits%20reviewing%20financial%20documents%2C%20dark%20elegant%20interior%20with%20warm%20golden%20accent%20lighting%2C%20premium%20corporate%20photography%2C%20sharp%20focus%2C%20high%20contrast%2C%20editorial%20style&width=800&height=1000&seq=about-exec-01&orientation=portrait"
                  alt="Reunión ejecutiva"
                  className="w-full h-[300px] md:h-[500px] object-cover object-top"
                />
              </div>
            </div>
            <div className="md:col-span-3 overflow-hidden rounded-2xl border border-aa-gold/20">
              <div ref={imageRightRef} className="parallax-image">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20infrastructure%20construction%20site%20in%20Latin%20America%2C%20large%20scale%20industrial%20development%20project%20at%20sunset%2C%20cranes%20and%20steel%20structures%20with%20golden%20hour%20lighting%2C%20dramatic%20sky%20with%20warm%20tones%2C%20professional%20architectural%20photography%2C%20high%20resolution%2C%20corporate%20investment%20project%20visual&width=1200&height=800&seq=about-infra-01&orientation=landscape"
                  alt="Infraestructura"
                  className="w-full h-[300px] md:h-[500px] object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}